import {
    addToast,
    Button,
    DatePicker,
    DateValue,
    Divider,
    Tab,
    Tabs,
} from "@heroui/react";
import UploadDataLayer from "./UploadDataLayer";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import { useCallback, useRef, useState } from "react";
import { Status } from "@/types/enums";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import useConfirm from "@/lib/confirm/useConfirm";
import {
    faArrowUpRightFromSquare,
    faDownload,
    faSquareArrowUpRight,
    faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormProvider, useForm } from "react-hook-form";
import AutoCompleteInput from "@/components/inputs/AutoCompleteInput";
import { ABLine, DataLayer } from "@/types/dataLayer";
import { ListRx } from "@/types/rx";
import classNames from "classnames";
import useAuth from "@/lib/auth/useAuth";
import { ImageryExtent } from "@/types/field";
import dayjs from "dayjs";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import shp from "shpjs";
import { getLocalTimeZone } from "@internationalized/date";
import { useTranslation } from "react-i18next";

function FieldSettings() {
    const { t } = useTranslation();
    const { userIsSentinel } = useAuth();
    const { useMutateRequest, downloadFile, useGetRequest } = useRequest();
    const { fieldQuery, fieldId, geometryQuery } = useFieldDashboard() ?? {};
    const navigate = useNavigate();
    const updateField = useMutateRequest({
        errorMessage: t("error.failed_to_update_field", { ns: "messages" }),
        successMessage: t("success.field_updated_successfully", {
            ns: "messages",
        }),
    });
    const queryclient = useQueryClient();
    const { showConfirm } = useConfirm();
    const deactivateField = useCallback(async () => {
        showConfirm({
            title: t("deactivate_field", { ns: "field" }),
            description: t("are_you_sure_you_want_to_deactivate_this_field", {
                ns: "field",
            }),
            color: "danger",
            onConfirm: async () => {
                await updateField.mutateAsync(
                    {
                        url: `/fields/${fieldId}/`,
                        method: "PUT",
                        data: {
                            status: Status.Inactive,
                            grower: fieldQuery?.data?.grower,
                            name: fieldQuery?.data?.name,
                        },
                    },
                    {
                        onSuccess: () => {
                            fieldQuery?.refetch();
                            queryclient.invalidateQueries({
                                queryKey: ["fields/"],
                                exact: true,
                                refetchType: "active",
                            });

                            navigate({
                                to: "/dashboard/fields",
                            });
                        },
                    },
                );
            },
        });
    }, [
        fieldId,
        fieldQuery,
        navigate,
        queryclient,
        showConfirm,
        t,
        updateField,
    ]);

    const downloadAllLayers = useCallback(async () => {
        await downloadFile(
            `/fields/${fieldId}/download_layers/`,
            `${fieldQuery?.data?.name} Geospatial Layers`,
        );
    }, [fieldId, fieldQuery?.data?.name, downloadFile]);

    const downloadFieldBoundary = useCallback(async () => {
        await downloadFile(
            `fields/${fieldId}/download_boundary/`,
            `${fieldQuery?.data?.name} Field Boundary.zip`,
        );
    }, [downloadFile, fieldId, fieldQuery?.data?.name]);

    const downloadIrrigationSystemBoundary = useCallback(async () => {
        await downloadFile(
            `irrigation_systems/${geometryQuery?.data?.irrigation_systems?.features[0]?.id}/download_boundary/`,
            `${fieldQuery?.data?.name} Irrigated Area Boundary.zip`,
        );
    }, [
        downloadFile,
        fieldQuery?.data?.name,
        geometryQuery?.data?.irrigation_systems?.features,
    ]);

    const downloadSectors = useCallback(async () => {
        if (!geometryQuery?.data?.irrigation_systems?.features?.length) return;

        await downloadFile(
            `irrigation_systems/${geometryQuery?.data?.irrigation_systems?.features[0]?.id}/download_sectors/`,
            `${fieldQuery?.data?.name} Sectors`,
        );
    }, [
        downloadFile,
        fieldQuery?.data?.name,
        geometryQuery?.data?.irrigation_systems?.features,
    ]);

    const managementZonesQuery = useGetRequest<
        {
            id: string;
            name: string;
            is_primary: boolean;
        }[]
    >({
        url: `fields/${fieldId}/management_zones/`,
        enabled: !!fieldId,
    });

    const abLinesQuery = useGetRequest<ABLine[]>({
        url: `fields/${fieldId}/ab_lines/`,
        enabled: !!fieldId,
    });

    const prescriptionsQuery = useGetRequest<ListRx[]>({
        url: `fields/${fieldId}/prescriptions/`,
        params: {
            type: "establishment",
        },
        enabled: !!fieldId,
    });

    const dataLayersQuery = useGetRequest<DataLayer[]>({
        url: `fields/${fieldId}/data_layers/`,
        enabled: !!fieldId,
    });

    const f = useForm<{
        managementZone: string;
        abLines: string;
        plots: string;
        dataLayers: string;
    }>({});

    const { watch } = f;
    const [managementZone, abLines, plots, dataLayers] = watch([
        "managementZone",
        "abLines",
        "plots",
        "dataLayers",
    ]);

    const onDownloadManagementZones = useCallback(async () => {
        const selectedManagementZone = managementZonesQuery?.data?.find(
            (mz) => mz.id == managementZone,
        );
        if (!selectedManagementZone) return;

        await downloadFile(
            `management_zones/${selectedManagementZone.id}/download/`,
            selectedManagementZone.name,
        );
    }, [downloadFile, managementZonesQuery?.data, managementZone]);

    const onDownloadABLines = useCallback(async () => {
        const selectedABLine = abLinesQuery?.data?.find(
            (abLine) => abLine.id?.toString() == abLines,
        );
        if (!selectedABLine) return;

        await downloadFile(
            `ab_lines/${selectedABLine.id}/download/`,
            selectedABLine.name,
        );
    }, [downloadFile, abLinesQuery?.data, abLines]);

    const onDownloadPlots = useCallback(async () => {
        const selectedPlot = prescriptionsQuery?.data?.find(
            (prescription) => prescription.id?.toString() == plots,
        );
        if (!selectedPlot) return;

        await downloadFile(
            `prescriptions/${selectedPlot.id}/plots/`,
            `${selectedPlot.name} Plots`,
        );
    }, [downloadFile, prescriptionsQuery?.data, plots]);

    const onDownloadDataLayers = useCallback(async () => {
        const selectedDataLayer = dataLayersQuery?.data?.find(
            (dataLayer) => dataLayer.id?.toString() == dataLayers,
        );
        if (!selectedDataLayer) return;

        await downloadFile(
            `data_layers/${selectedDataLayer.id}/`,
            selectedDataLayer.name,
        );
    }, [downloadFile, dataLayersQuery?.data, dataLayers]);

    const activeEnrollment = fieldQuery?.data?.active_enrollment;

    const { samplesEnabled } = useFeatureFlag();

    const uploadMutate = useMutateRequest({
        errorMessage: t("error.error_uploading_samples"),
        successMessage: t("success.samples_uploaded_successfully"),
    });

    const [soilDate, setSoilDate] = useState<DateValue | null>(null);
    const [tissueDate, setTissueDate] = useState<DateValue | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    return (
        <div className="flex flex-col gap-2">
            <div>
                <Button size="sm" color="danger" onPress={deactivateField}>
                    {t("deactivate_field", { ns: "field" })}
                </Button>
            </div>
            <Tabs fullWidth>
                {userIsSentinel && (
                    <Tab key="1" title={t("enrollment", { ns: "field" })}>
                        {activeEnrollment && (
                            <>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        {t("enrollment_status", {
                                            ns: "field",
                                        })}
                                    </div>
                                    <div
                                        className={classNames({
                                            "text-green-500": activeEnrollment,
                                            "text-red-500": !activeEnrollment,
                                        })}
                                    >
                                        {activeEnrollment
                                            ? t("enrolled", { ns: "field" })
                                            : t("not_enrolled", {
                                                  ns: "field",
                                              })}
                                    </div>
                                    <div>
                                        {t("imagery_boundary", { ns: "field" })}
                                    </div>
                                    <div>
                                        {fieldQuery?.data?.imagery_extent ==
                                        ImageryExtent.FIELD
                                            ? t("field", { ns: "field" })
                                            : t("irrigated", { ns: "field" })}
                                    </div>
                                    <div>
                                        {t("start_date", { ns: "field" })}
                                    </div>
                                    <div>
                                        {dayjs(
                                            activeEnrollment?.start_date,
                                        ).format("ll")}
                                    </div>
                                    <div>{t("end_date", { ns: "field" })}</div>
                                    <div>
                                        {dayjs(
                                            activeEnrollment?.end_date,
                                        ).format("ll")}
                                    </div>
                                    <div>
                                        {t("insights_start_date", {
                                            ns: "field",
                                        })}
                                    </div>
                                    <div>
                                        {dayjs(
                                            activeEnrollment?.insights_start_date,
                                        ).format("ll")}
                                    </div>
                                    <div>
                                        {t("insights_end_date", {
                                            ns: "field",
                                        })}
                                    </div>
                                    <div>
                                        {dayjs(
                                            activeEnrollment?.insights_end_date,
                                        ).format("ll")}
                                    </div>
                                </div>
                                <div className="pt-4">
                                    <Button
                                        as={Link}
                                        to="/dashboard/enrollment"
                                        startContent={
                                            <FontAwesomeIcon
                                                icon={faSquareArrowUpRight}
                                            />
                                        }
                                        color="primary"
                                    >
                                        {t("go_to_enrollment", { ns: "field" })}
                                    </Button>
                                </div>
                            </>
                        )}
                        {!activeEnrollment && (
                            <div className="flex flex-col gap-2">
                                {t("no_enrollment_found", { ns: "field" })}
                                <Link to="/dashboard/enrollment/fields">
                                    <Button
                                        color="primary"
                                        startContent={
                                            <FontAwesomeIcon
                                                icon={faArrowUpRightFromSquare}
                                            />
                                        }
                                    >
                                        {t("go_to_enrollment", { ns: "field" })}
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </Tab>
                )}

                <Tab key="2" title={t("data", { ns: "field" })}>
                    <FormProvider {...f}>
                        <form>
                            <UploadDataLayer />
                            <div className="flex flex-col gap-2">
                                <div className="text-lg font-semibold mt-4">
                                    {t("download_data_layers", { ns: "field" })}
                                </div>
                                <div className="flex flex-row gap-2 justify-between items-center">
                                    <div className="text-sm font-semibold">
                                        {t("all_geospatial_layers", {
                                            ns: "field",
                                        })}
                                    </div>
                                    <div>
                                        <Button
                                            size="sm"
                                            onPress={downloadAllLayers}
                                            color="primary"
                                            isIconOnly
                                        >
                                            <FontAwesomeIcon
                                                icon={faDownload}
                                            />
                                        </Button>
                                    </div>
                                </div>
                                <Divider />
                                <div className="flex flex-row gap-2 justify-between items-center">
                                    <div className="text-sm font-semibold">
                                        {t("field_boundary", { ns: "field" })}
                                    </div>
                                    <div>
                                        <Button
                                            size="sm"
                                            onPress={downloadFieldBoundary}
                                            color="primary"
                                            isIconOnly
                                        >
                                            <FontAwesomeIcon
                                                icon={faDownload}
                                            />
                                        </Button>
                                    </div>
                                </div>
                                <Divider />

                                <div className="flex flex-row gap-2 justify-between items-center">
                                    <div className="text-sm font-semibold">
                                        {t("irrigated_area_boundary", {
                                            ns: "field",
                                        })}
                                    </div>
                                    <div>
                                        <Button
                                            size="sm"
                                            onPress={
                                                downloadIrrigationSystemBoundary
                                            }
                                            color="primary"
                                            isIconOnly
                                            isDisabled={
                                                !geometryQuery?.data
                                                    ?.irrigation_systems
                                                    ?.features?.length
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={faDownload}
                                            />
                                        </Button>
                                    </div>
                                </div>
                                <Divider />
                                <div className="flex flex-row gap-2 justify-between items-center">
                                    <div className="text-sm font-semibold">
                                        {t("management_zones", { ns: "field" })}
                                    </div>
                                    <div className="flex flex-row gap-2 items-center">
                                        <AutoCompleteInput
                                            name="managementZone"
                                            aria-label="management zone"
                                            options={
                                                managementZonesQuery?.data?.map(
                                                    (mz) => ({
                                                        id: mz.id,
                                                        description: mz.name,
                                                    }),
                                                ) ?? []
                                            }
                                        />
                                        <Button
                                            size="sm"
                                            onPress={onDownloadManagementZones}
                                            color="primary"
                                            isIconOnly
                                            isDisabled={!managementZone}
                                        >
                                            <FontAwesomeIcon
                                                icon={faDownload}
                                            />
                                        </Button>
                                    </div>
                                </div>
                                <Divider />
                                <div className="flex flex-row gap-2 justify-between items-center">
                                    <div className="text-sm font-semibold">
                                        {t("ab_lines", { ns: "field" })}
                                    </div>
                                    <div className="flex flex-row gap-2 items-center">
                                        <AutoCompleteInput
                                            name="abLines"
                                            aria-label="ab lines"
                                            options={
                                                abLinesQuery?.data?.map(
                                                    (abLine) => ({
                                                        id: abLine.id,
                                                        description:
                                                            abLine.name,
                                                    }),
                                                ) ?? []
                                            }
                                        />
                                        <Button
                                            size="sm"
                                            onPress={onDownloadABLines}
                                            color="primary"
                                            isIconOnly
                                            isDisabled={!abLines}
                                        >
                                            <FontAwesomeIcon
                                                icon={faDownload}
                                            />
                                        </Button>
                                    </div>
                                </div>
                                <Divider />
                                <div className="flex flex-row gap-2 justify-between items-center">
                                    <div className="text-sm font-semibold">
                                        {t("plots", { ns: "field" })}
                                    </div>
                                    <div className="flex flex-row gap-2 items-center">
                                        <AutoCompleteInput
                                            name="plots"
                                            aria-label="plots"
                                            options={
                                                prescriptionsQuery?.data?.map(
                                                    (prescription) => ({
                                                        id: prescription.id,
                                                        description:
                                                            prescription.name,
                                                    }),
                                                ) ?? []
                                            }
                                        />
                                        <Button
                                            size="sm"
                                            onPress={onDownloadPlots}
                                            color="primary"
                                            isIconOnly
                                            isDisabled={!plots}
                                        >
                                            <FontAwesomeIcon
                                                icon={faDownload}
                                            />
                                        </Button>
                                    </div>
                                </div>
                                <Divider />

                                <div className="flex flex-row gap-2 justify-between items-center">
                                    <div className="text-sm font-semibold">
                                        {t("treatment_sectors", {
                                            ns: "field",
                                        })}
                                    </div>
                                    <div>
                                        <Button
                                            size="sm"
                                            onPress={downloadSectors}
                                            color="primary"
                                            isIconOnly
                                            isDisabled={
                                                !geometryQuery?.data
                                                    ?.treatment_sections
                                                    ?.features?.length
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={faDownload}
                                            />
                                        </Button>
                                    </div>
                                </div>
                                <Divider />

                                <div className="flex flex-row gap-2 justify-between items-center">
                                    <div className="text-sm font-semibold">
                                        {t("data_layers", { ns: "field" })}
                                    </div>
                                    <div className="flex flex-row gap-2 items-center">
                                        <AutoCompleteInput
                                            name="dataLayers"
                                            aria-label="data layers"
                                            options={
                                                dataLayersQuery?.data?.map(
                                                    (dataLayer) => ({
                                                        id: dataLayer.id,
                                                        description:
                                                            dataLayer.name,
                                                    }),
                                                ) ?? []
                                            }
                                        />
                                        <Button
                                            size="sm"
                                            onPress={onDownloadDataLayers}
                                            color="primary"
                                            isIconOnly
                                            isDisabled={!dataLayers}
                                        >
                                            <FontAwesomeIcon
                                                icon={faDownload}
                                            />
                                        </Button>
                                    </div>
                                </div>

                                {samplesEnabled && (
                                    <>
                                        <Divider />
                                        <div className="text-sm font-semibold">
                                            {t("soil_samples", { ns: "field" })}
                                        </div>
                                        <div className="flex flex-row gap-2 justify-start items-end">
                                            <div>
                                                <DatePicker
                                                    label={t(
                                                        "soil_sample_date",
                                                        { ns: "field" },
                                                    )}
                                                    labelPlacement="outside"
                                                    onChange={setSoilDate}
                                                    value={soilDate}
                                                    size="sm"
                                                />
                                            </div>
                                            <div>
                                                <Button
                                                    size="sm"
                                                    color="primary"
                                                    startContent={
                                                        <FontAwesomeIcon
                                                            icon={faUpload}
                                                        />
                                                    }
                                                    isDisabled={!soilDate}
                                                    onPress={() => {
                                                        if (
                                                            fileInputRef.current
                                                        ) {
                                                            fileInputRef.current.click();
                                                        }
                                                    }}
                                                >
                                                    <input
                                                        type="file"
                                                        hidden
                                                        ref={fileInputRef}
                                                        onChange={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            const files =
                                                                e.target.files;
                                                            if (
                                                                files &&
                                                                files.length > 0
                                                            ) {
                                                                const file =
                                                                    files[0];
                                                                const reader =
                                                                    new FileReader();
                                                                reader.onload =
                                                                    async (
                                                                        event,
                                                                    ) => {
                                                                        try {
                                                                            const arrayBuffer =
                                                                                event
                                                                                    ?.target
                                                                                    ?.result;
                                                                            if (
                                                                                !arrayBuffer
                                                                            ) {
                                                                                throw new Error(
                                                                                    "No ArrayBuffer result from FileReader",
                                                                                );
                                                                            }
                                                                            const geojson =
                                                                                await shp(
                                                                                    arrayBuffer,
                                                                                );
                                                                            const features =
                                                                                (Array.isArray(
                                                                                    geojson,
                                                                                )
                                                                                    ? geojson[0]
                                                                                          ?.features
                                                                                    : geojson.features) ||
                                                                                geojson;

                                                                            uploadMutate.mutate(
                                                                                {
                                                                                    url: `/soil_samples/`,
                                                                                    method: "post",
                                                                                    data: {
                                                                                        file_name:
                                                                                            file.name,
                                                                                        field: fieldId,
                                                                                        sample_date:
                                                                                            soilDate
                                                                                                ? dayjs(
                                                                                                      soilDate.toDate(
                                                                                                          getLocalTimeZone(),
                                                                                                      ),
                                                                                                  ).format(
                                                                                                      "YYYY-MM-DD",
                                                                                                  )
                                                                                                : null,
                                                                                        soil_samples:
                                                                                            features?.map(
                                                                                                (
                                                                                                    x,
                                                                                                ) => ({
                                                                                                    ...x,
                                                                                                    properties:
                                                                                                        {
                                                                                                            ...x.properties,
                                                                                                            soil_properties:
                                                                                                                x.properties,
                                                                                                        },
                                                                                                }),
                                                                                            ),
                                                                                    },
                                                                                },
                                                                            );
                                                                        } catch {
                                                                            addToast(
                                                                                {
                                                                                    title: t(
                                                                                        "addToast.error",
                                                                                    ),
                                                                                    description:
                                                                                        t(
                                                                                            "addToast.error_reading_shapefile",
                                                                                        ),
                                                                                    color: "danger",
                                                                                },
                                                                            );
                                                                        }
                                                                    };
                                                                reader.readAsArrayBuffer(
                                                                    file,
                                                                );
                                                            }
                                                        }}
                                                    />
                                                    {t("upload_soil_sample", {
                                                        ns: "field",
                                                    })}
                                                </Button>
                                            </div>
                                        </div>
                                        <Divider />
                                        <div className="text-sm font-semibold">
                                            {t("tissue_samples", {
                                                ns: "field",
                                            })}
                                        </div>
                                        <div className="flex flex-row gap-2 justify-start items-end">
                                            <div>
                                                <DatePicker
                                                    label={t(
                                                        "tissue_sample_date",
                                                        { ns: "field" },
                                                    )}
                                                    labelPlacement="outside"
                                                    onChange={setTissueDate}
                                                    value={tissueDate}
                                                    size="sm"
                                                />
                                            </div>
                                            <div>
                                                <Button
                                                    size="sm"
                                                    color="primary"
                                                    startContent={
                                                        <FontAwesomeIcon
                                                            icon={faUpload}
                                                        />
                                                    }
                                                    isDisabled={!tissueDate}
                                                    onPress={() => {
                                                        if (
                                                            fileInputRef.current
                                                        ) {
                                                            fileInputRef.current.click();
                                                        }
                                                    }}
                                                >
                                                    <input
                                                        type="file"
                                                        hidden
                                                        ref={fileInputRef}
                                                        onChange={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            const files =
                                                                e.target.files;
                                                            if (
                                                                files &&
                                                                files.length > 0
                                                            ) {
                                                                const file =
                                                                    files[0];
                                                                const reader =
                                                                    new FileReader();
                                                                reader.onload =
                                                                    async (
                                                                        event,
                                                                    ) => {
                                                                        try {
                                                                            const arrayBuffer =
                                                                                event
                                                                                    ?.target
                                                                                    ?.result;
                                                                            if (
                                                                                !arrayBuffer
                                                                            ) {
                                                                                throw new Error(
                                                                                    "No ArrayBuffer result from FileReader",
                                                                                );
                                                                            }
                                                                            const geojson =
                                                                                await shp(
                                                                                    arrayBuffer,
                                                                                );
                                                                            const features =
                                                                                (Array.isArray(
                                                                                    geojson,
                                                                                )
                                                                                    ? geojson[0]
                                                                                          ?.features
                                                                                    : geojson.features) ||
                                                                                geojson;
                                                                            uploadMutate.mutate(
                                                                                {
                                                                                    url: `/tissue_samples/`,
                                                                                    method: "post",
                                                                                    data: {
                                                                                        file_name:
                                                                                            file.name,
                                                                                        field: fieldId,
                                                                                        sample_date:
                                                                                            tissueDate
                                                                                                ? dayjs(
                                                                                                      tissueDate.toDate(
                                                                                                          getLocalTimeZone(),
                                                                                                      ),
                                                                                                  ).format(
                                                                                                      "YYYY-MM-DD",
                                                                                                  )
                                                                                                : null,
                                                                                        tissue_samples:
                                                                                            features?.map(
                                                                                                (
                                                                                                    x,
                                                                                                ) => ({
                                                                                                    ...x,
                                                                                                    properties:
                                                                                                        {
                                                                                                            ...x.properties,
                                                                                                            tissue_properties:
                                                                                                                x.properties,
                                                                                                        },
                                                                                                }),
                                                                                            ),
                                                                                    },
                                                                                },
                                                                            );
                                                                        } catch {
                                                                            addToast(
                                                                                {
                                                                                    title: t(
                                                                                        "addToast.error",
                                                                                    ),
                                                                                    description:
                                                                                        t(
                                                                                            "addToast.error_reading_shapefile",
                                                                                        ),
                                                                                    color: "danger",
                                                                                },
                                                                            );
                                                                        }
                                                                    };
                                                                reader.readAsArrayBuffer(
                                                                    file,
                                                                );
                                                            }
                                                        }}
                                                    />
                                                    {t("upload_tissue_sample", {
                                                        ns: "field",
                                                    })}
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </form>
                    </FormProvider>
                </Tab>
            </Tabs>
        </div>
    );
}

export default FieldSettings;
